import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, User, FileText, Phone, Mail, MapPin } from 'lucide-react';
import type { Driver, DriverForm, ModalState } from '../types/system-parameters.types';

interface DriverModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: DriverForm, isEdit?: boolean, itemId?: string) => void;
}

const licenseTypeOptions = [
  { value: 'A1', label: 'A1 - Motocicleta hasta 125cc' },
  { value: 'A2a', label: 'A2a - Motocicleta 126cc-250cc' },
  { value: 'A2b', label: 'A2b - Motocicleta más de 250cc' },
  { value: 'A3a', label: 'A3a - Mototaxi' },
  { value: 'A3b', label: 'A3b - Trimoto de carga' },
  { value: 'A3c', label: 'A3c - Cuatrimoto' },
  { value: 'B1', label: 'B1 - Automóvil' },
  { value: 'B2a', label: 'B2a - Camioneta hasta 3,500kg' },
  { value: 'B2b', label: 'B2b - Camión más de 3,500kg' },
  { value: 'B2c', label: 'B2c - Ómnibus' }
];


export const DriverModal: React.FC<DriverModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<DriverForm>({
    fullName: '',
    licenseNumber: '',
    licenseType: '',
    licenseExpiry: '',
    phone: '',
    email: '',
    address: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const driver = modalState.item as Driver;
        setFormData({
          fullName: driver.additionalData.fullName,
          licenseNumber: driver.additionalData.licenseNumber,
          licenseType: driver.additionalData.licenseType,
          licenseExpiry: driver.additionalData.licenseExpiry,
          phone: driver.additionalData.phone || '',
          email: driver.additionalData.email || '',
          address: driver.additionalData.address || ''
        });
      } else {
        // Resetear para modo crear
        setFormData({
          fullName: '',
          licenseNumber: '',
          licenseType: '',
          licenseExpiry: '',
          phone: '',
          email: '',
          address: ''
        });
      }
      setErrors({});
    }
  }, [modalState]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validaciones obligatorias
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'El nombre completo es obligatorio';
    }

    if (!formData.licenseNumber.trim()) {
      newErrors.licenseNumber = 'El número de licencia es obligatorio';
    } else if (!/^[A-Z]\d{8}$/.test(formData.licenseNumber.trim())) {
      newErrors.licenseNumber = 'Formato inválido (ej: Q12345678)';
    }

    if (!formData.licenseType) {
      newErrors.licenseType = 'El tipo de licencia es obligatorio';
    }

    if (!formData.licenseExpiry) {
      newErrors.licenseExpiry = 'La fecha de vencimiento es obligatoria';
    } else {
      const expiryDate = new Date(formData.licenseExpiry);
      const today = new Date();
      if (expiryDate < today) {
        newErrors.licenseExpiry = 'La fecha de vencimiento no puede estar en el pasado';
      }
    }

    // Validaciones opcionales pero con formato
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    if (formData.phone && !/^\+51\s9\d{8}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Formato inválido (ej: +51 987654321)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const isEdit = modalState.mode === 'edit';
      const itemId = modalState.item?.id;
      
      onSave(formData, isEdit, itemId);
      onClose();
    } catch (error) {
      console.error('Error al guardar conductor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof DriverForm, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-macartur-primary" />
            {modalState.mode === 'edit' ? 'Editar Conductor' : 'Agregar Nuevo Conductor'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Personal */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Información Personal
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">
                  Nombre Completo <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Juan Carlos Pérez López"
                  className={errors.fullName ? 'border-red-500' : ''}
                />
                {errors.fullName && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.fullName}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+51 987654321"
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.phone}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="conductor@macartur.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Av. Los Pinos 123, San Borja"
                />
              </div>
            </div>
          </div>

          {/* Información de Licencia */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Información de Licencia
            </h3>
            
            {/* Primera fila: Tipo de Licencia */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="licenseType">
                  Tipo de Licencia <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.licenseType} 
                  onValueChange={(value) => handleInputChange('licenseType', value)}
                >
                  <SelectTrigger className={errors.licenseType ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {licenseTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.licenseType && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.licenseType}
                  </p>
                )}
              </div>
            </div>

            {/* Segunda fila: Número de Licencia y Fecha de Vencimiento */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="licenseNumber">
                  Número de Licencia <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={(e) => handleInputChange('licenseNumber', e.target.value.toUpperCase())}
                  placeholder="Q12345678"
                  className={errors.licenseNumber ? 'border-red-500' : ''}
                  maxLength={9}
                />
                {errors.licenseNumber && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.licenseNumber}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="licenseExpiry">
                  Fecha de Vencimiento <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="licenseExpiry"
                  type="date"
                  value={formData.licenseExpiry}
                  onChange={(e) => handleInputChange('licenseExpiry', e.target.value)}
                  className={errors.licenseExpiry ? 'border-red-500' : ''}
                />
                {errors.licenseExpiry && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.licenseExpiry}
                  </p>
                )}
              </div>
            </div>
          </div>


          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-macartur-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (modalState.mode === 'edit' ? 'Actualizar' : 'Agregar')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};